import {
  MessageBar,
  MessageBarType,
  Stack,
} from '@fluentui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import useFetch from 'use-http';
import { API_BASE_URL, UPLOAD_AS_SUPPORTING_FILES } from '../../../config';
import { useIntl } from 'react-intl';
import messages from './messages';
import { ProjectSearchResponse } from '../types';
import { useDebouncedCallback } from '../../../hooks/useDebouncedCallback';
import { useOrganizationsStore } from '../../organizations/store';
import { FeatureFlags } from '../../organizations';
import { Combobox, ComboboxProps, Option, Text } from '@fluentui/react-components';


export const ProjectSelect = ({
  organizationId,
  isDisabled,
  setSelectedProject,
  selectedProject = { key: null, text: '', title: '' },
}: {
  organizationId: number | string;
  isDisabled: boolean;
  setSelectedProject: (t: ProjectOption) => void;
  selectedProject: ProjectOption;
}): JSX.Element => {
  const { post, error, data, response } = useFetch<ProjectSearchResponse>(
    API_BASE_URL() + `/organizations/${organizationId}/projects/search`,
    {},
  );

  const [projectOptions, setProjectOptions] = useState<ProjectOption[]>([]);

  const intl = useIntl();
  const { currentOrganizationSettings } = useOrganizationsStore();

  const [inputValue, setInputValue] = useState('');

  const matterStatusFilter = useMemo(() => {
    if (UPLOAD_AS_SUPPORTING_FILES) { // skip filtering when uploading all files as supporting files
      return []
    }
    if (currentOrganizationSettings) {
      return currentOrganizationSettings[
        FeatureFlags.CAN_MANAGE_DOCUMENTS_DURING_FINAL_APPROVAL
      ]
        ? [{k:'matter_status', vs:['draft', 'review', 'initial_approval', 'final_approval']}]
        : [{k:'matter_status', vs:['draft', 'review', 'initial_approval']}];
    }
    return [{k:'matter_status', vs:['draft', 'review', 'initial_approval']}];
  }, [currentOrganizationSettings]);

  const postSearchAsync = useCallback(
    async searchText => {
      try {
        const response = await post({
          searchText,
          filters: matterStatusFilter,
          projectDetailLevel: 'list',
        });
        if (response) {
          const projectOptions: ProjectOption[] = response.map(project => {
            return {
              key: `${project.id}`,
              text: project.title,
              title: project.title,
            };
          });
          setProjectOptions(projectOptions);
        }
      } catch (e) {
        // TODO: Error handled in component
        console.error(e);
      }
    },
    [organizationId, post, matterStatusFilter],
  );

  const debouncedSearch = useDebouncedCallback(postSearchAsync, 300);

  useEffect(() => {
    if (isDisabled || !organizationId) return;

    const postAsync = async () => {
      try {
        postSearchAsync('');
      } catch (e) {
        console.error(e);
      }
    };
    postAsync();
  }, [organizationId, isDisabled, post]);

  const handleOptionChange: ComboboxProps['onOptionSelect'] = (
    _event,
    data,
  ) => {
    let key = data.optionValue;
    const text = data.optionText;

    const projectOption = projectOptions.find(option => option.key === key);

    if (projectOption) {
      setSelectedProject(projectOption);
      setInputValue(text);
    }
  };

  const onInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!organizationId) return;
    const searchText = event.target.value;
    debouncedSearch(searchText);
    setInputValue(searchText);
  };

  const placeholder =
    data === null && response.ok
      ? intl.formatMessage(messages.noProjectsFound)
      : intl.formatMessage(messages.selectProjectLabel);

  return (
    <>
      {!error && projectOptions && (
        <Stack>
          <label htmlFor="project-select" style={{
            fontWeight: 600,
            marginBottom: "4px"
          }}>
            {intl.formatMessage(messages.selectProjectLabel)}
          </label>
          <Combobox
            id="project-select"
            selectedOptions={[String(selectedProject.key)]}
            onOptionSelect={handleOptionChange}
            onInput={onInput}
            value={inputValue}
            autoComplete="off"
            freeform
            placeholder={placeholder}
            disabled={isDisabled}
          >
            {
              projectOptions.map((option) => {
                return <Option key={option.key} text={option.text} value={option.key as string}>
                  <Text truncate wrap={false}>
                    {option.text}
                  </Text>
                </Option>
              })
            }
          </Combobox>
        </Stack>
      )}
      {error && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={true}
          dismissButtonAriaLabel={intl.formatMessage(messages.close)}>
          <p>{intl.formatMessage(messages.errorLoadingProjects)}</p>
        </MessageBar>
      )}
    </>
  );
};
